import { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { Empty, FormDialog, PageHead, money, useApi } from "@/components/shop/common";

const FIELDS = [
  { name: "name", label: "Part name", required: true },
  { name: "part_number", label: "Part #", half: true },
  { name: "brand", label: "Brand", half: true },
  { name: "qty", label: "Qty on hand", type: "number", half: true },
  { name: "min_qty", label: "Reorder at", type: "number", half: true },
  { name: "cost", label: "Cost", type: "number", half: true },
  { name: "price", label: "Sell price", type: "number", half: true },
  { name: "location", label: "Bin / location" },
];

export default function Inventory() {
  const [items, load] = useApi("/inventory");
  const [edit, setEdit] = useState(null);
  const [q, setQ] = useState("");
  const save = async (v) => { if (edit.id) await api.put(`/inventory/${edit.id}`, v); else await api.post("/inventory", v); toast.success("Part saved"); load(); };
  const del = async (it) => { if (window.confirm(`Delete ${it.name}?`)) { await api.delete(`/inventory/${it.id}`); load(); } };
  const shown = (items || []).filter((i) => `${i.name} ${i.part_number} ${i.brand}`.toLowerCase().includes(q.toLowerCase()));
  const value = (items || []).reduce((a, i) => a + i.qty * i.cost, 0);

  return (
    <>
      <PageHead eyebrow="Parts room" title="Inventory">
        <Button onClick={() => setEdit({ qty: 0, min_qty: 1, cost: 0, price: 0 })} className="rounded-sm hover:bg-orange-600 hover:text-white" data-testid="add-part-btn"><Plus className="h-4 w-4 mr-1" /> Part</Button>
      </PageHead>
      <div className="flex flex-wrap gap-4 items-center justify-between mb-4">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search parts…" className="h-10 px-3 rounded-sm bg-card border border-input text-sm w-full sm:w-80" data-testid="inventory-search-input" />
        <p className="font-mono text-xs text-muted-foreground" data-testid="inventory-value">{items?.length || 0} SKUs · stock value {money(value)}</p>
      </div>
      {items?.length === 0 ? <Empty testid="inventory-empty">No parts in stock yet.</Empty> : (
        <div className="border border-border rounded-sm bg-card/80 overflow-x-auto">
          <table className="w-full text-sm" data-testid="inventory-table">
            <thead className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              <tr className="border-b border-border">{["Part", "Part #", "Brand", "Bin", "Qty", "Reorder", "Cost", "Price", ""].map((h) => <th key={h} className="text-left p-3 font-normal">{h}</th>)}</tr>
            </thead>
            <tbody>
              {shown.map((i) => {
                const low = i.qty <= i.min_qty;
                return (
                  <tr key={i.id} className={`border-b border-border last:border-0 ${low ? "bg-red-500/5" : ""}`} data-testid={`inventory-row-${i.id}`}>
                    <td className="p-3 font-semibold">{i.name}</td>
                    <td className="p-3 font-mono text-amber-400">{i.part_number || "—"}</td>
                    <td className="p-3">{i.brand || "—"}</td>
                    <td className="p-3 text-muted-foreground">{i.location || "—"}</td>
                    <td className={`p-3 font-mono ${low ? "text-red-400" : ""}`} data-testid={`inventory-qty-${i.id}`}>{low && <AlertTriangle className="inline h-3.5 w-3.5 mr-1" />}{i.qty}</td>
                    <td className="p-3 font-mono text-muted-foreground">{i.min_qty}</td>
                    <td className="p-3 font-mono">{money(i.cost)}</td>
                    <td className="p-3 font-mono">{money(i.price)}</td>
                    <td className="p-3 whitespace-nowrap">
                      <button onClick={() => setEdit(i)} className="text-muted-foreground hover:text-white mr-3" data-testid={`inventory-edit-${i.id}`}><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => del(i)} className="text-muted-foreground hover:text-red-400" data-testid={`inventory-delete-${i.id}`}><Trash2 className="h-4 w-4" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <FormDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} title={edit?.id ? "Edit part" : "New part"} fields={FIELDS} initial={edit} onSubmit={save} testid="inventory-dialog" />
    </>
  );
}
